import React, { useState, useRef, useEffect, useCallback } from 'react';
import ProcessMessageContent from './ProcessMessageContent';
import TextWithTables from '../common/TextWithTables';
import CitationTooltip from '../common/CitationTooltip';
import { renderTextWithCitations } from '../../utils/citationFormatter';
import { apiService } from '../../services/api';

const PROMPT_HISTORY_LS_KEY = (graphId) => `testGraph_promptHistory_${graphId}`;
const PROMPT_HISTORY_MAX = 50;

// Default suggested questions shown on the empty/welcome state. These are
// tuned for the Denial Mitigation Router graph (the primary embed target)
// but make sense for any healthcare denial-management graph. The host page
// (or the embed launcher) can override by setting
// window.__CM_CHAT_SUGGESTED_QUESTIONS__ before the chat mounts.
const DEFAULT_SUGGESTED_QUESTIONS = [
  'What is a CO-50 denial and how do I appeal it?',
  'List the most common reasons for a CO-97 denial.',
  'Which modifiers bypass NCCI edits for CPT 64483?',
  'What evidence does a medical-necessity appeal letter need?',
  'How do I prevent timely-filing denials for Medicare?',
  'What is the difference between CO-45 and PR-204?',
];

const TestGraphChat = ({ 
  graphId,
  messages, 
  onSendMessage, 
  conversationHistory, 
  onViewInsight,
  onExplain,
  onSelectRouterGraph,
  welcomeMessage = null,
  suggestedQuestions: suggestedQuestionsProp = null,
  routerTargets = null,
  selectedGraphIds = null,
  onGraphSelectionChange = null,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [graphSelectorOpen, setGraphSelectorOpen] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const historyRef = useRef(null);
  const graphSelectorRef = useRef(null);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Listen for `cm:fill-input` postMessage events from the embed host
  // (e.g. the DM/RCMBoost workflow shell's "Send to chatbox" button).
  // The host iframe is cross-origin so it can't type into the input
  // directly; instead it posts the case-context text and we drop it
  // into the textarea so the user can review + press Enter.
  useEffect(() => {
    const onMessage = (event) => {
      const data = event?.data;
      if (!data || typeof data !== 'object') return;
      if (data.type !== 'cm:fill-input') return;
      if (typeof data.text !== 'string' || !data.text.trim()) return;
      setInputValue(data.text);
      try {
        inputRef.current?.focus();
        const el = inputRef.current;
        if (el) {
          const end = data.text.length;
          el.setSelectionRange(end, end);
          el.scrollTop = el.scrollHeight;
        }
      } catch { /* ignore */ }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // Close graph selector dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (graphSelectorRef.current && !graphSelectorRef.current.contains(e.target)) {
        setGraphSelectorOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Hydrate prompt history from localStorage when graph changes
  useEffect(() => {
    if (!graphId) {
      setHistory([]);
      return;
    }
    try {
      const raw = localStorage.getItem(PROMPT_HISTORY_LS_KEY(graphId));
      const parsed = raw ? JSON.parse(raw) : [];
      setHistory(Array.isArray(parsed) ? parsed : []);
    } catch (e) {
      setHistory([]);
    }
  }, [graphId]);

  // Close the dropdown when clicking outside
  useEffect(() => {
    if (!historyOpen) return undefined;
    const onClickOutside = (e) => {
      if (historyRef.current && !historyRef.current.contains(e.target)) {
        setHistoryOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [historyOpen]);

  const recordPrompt = useCallback((prompt) => {
    if (!graphId) return;
    const trimmed = (prompt || '').trim();
    if (!trimmed) return;
    setHistory((prev) => {
      const without = prev.filter((p) => p && p.text !== trimmed);
      const next = [{ text: trimmed, ts: Date.now() }, ...without].slice(0, PROMPT_HISTORY_MAX);
      try {
        localStorage.setItem(PROMPT_HISTORY_LS_KEY(graphId), JSON.stringify(next));
      } catch (e) {
        // localStorage may be full or disabled -- ignore.
      }
      return next;
    });
  }, [graphId]);

  // Handle send. Accepts an optional explicit query so suggested-question
  // chips can submit without round-tripping through inputValue.
  const handleSend = async (explicitQuery) => {
    const candidate = typeof explicitQuery === 'string' ? explicitQuery : inputValue;
    const query = (candidate || '').trim();
    if (!query || isLoading) return;

    recordPrompt(query);
    setIsLoading(true);
    setInputValue('');
    setHistoryOpen(false);

    try {
      await onSendMessage(query);
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  // Resolve the suggested questions for the empty state. Priority:
  //   1. explicit prop (e.g. a published Chat Assistant's config)
  //   2. window.__CM_CHAT_SUGGESTED_QUESTIONS__ (embed host override)
  //   3. built-in defaults
  const suggestedQuestions = (() => {
    if (Array.isArray(suggestedQuestionsProp) && suggestedQuestionsProp.length > 0) {
      return suggestedQuestionsProp.filter((q) => typeof q === 'string' && q.trim()).slice(0, 8);
    }
    try {
      const override = typeof window !== 'undefined'
        ? window.__CM_CHAT_SUGGESTED_QUESTIONS__
        : null;
      if (Array.isArray(override) && override.length > 0) {
        return override.filter((q) => typeof q === 'string' && q.trim()).slice(0, 8);
      }
    } catch (e) { /* ignore */ }
    return DEFAULT_SUGGESTED_QUESTIONS;
  })();

  // Handle Enter key
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="test-graph-chat">
      {/* Messages Container */}
      <div className="chat-messages">
        {messages.length === 0 ? (
          <div className="chat-empty">
            <div className="chat-empty-icon">💬</div>
            {welcomeMessage ? (
              <p className="chat-welcome-message">{welcomeMessage}</p>
            ) : (
              <>
                <h3>Test Your Graph</h3>
                <p>
                  Ask questions about the knowledge in your graph. 
                  The Platform engine will activate relevant nodes and generate answers.
                </p>
              </>
            )}
            {suggestedQuestions.length > 0 && (
              <div className="chat-suggestions" aria-label="Example questions">
                <div className="chat-suggestions-label">Try one of these</div>
                <div className="chat-suggestions-list">
                  {suggestedQuestions.map((q) => (
                    <button
                      key={q}
                      type="button"
                      className="chat-suggestion-chip"
                      disabled={isLoading}
                      onClick={() => handleSend(q)}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {!welcomeMessage && (
              <div className="chat-tips">
                <div className="tip">
                  <strong>Tip:</strong> Start with a specific question about the domain.
                </div>
                <div className="tip">
                  <strong>Context:</strong> Follow-up questions will include conversation history.
                </div>
              </div>
            )}
          </div>
        ) : (
          <>
            {messages.map((message) => (
              <ChatMessage 
                key={message.id} 
                message={message} 
                onViewInsight={onViewInsight}
                onExplain={onExplain}
                onSelectRouterGraph={onSelectRouterGraph}
              />
            ))}
          </>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Context Indicator */}
      {conversationHistory.length > 0 && (
        <div className="context-indicator">
          <span className="context-icon">🔗</span>
          <span>Context: {conversationHistory.length} messages in conversation</span>
        </div>
      )}

      {/* Graph Selector (published chat with router) */}
      {routerTargets && routerTargets.length > 0 && onGraphSelectionChange && (
        <div className="graph-selector-bar" ref={graphSelectorRef}>
          <button
            type="button"
            className="graph-selector-toggle"
            onClick={() => setGraphSelectorOpen((v) => !v)}
          >
            <span className="graph-selector-icon">🔀</span>
            <span className="graph-selector-label">
              {!selectedGraphIds || selectedGraphIds.size === 0
                ? 'Auto Router'
                : selectedGraphIds.size === 1
                  ? routerTargets.find((t) => selectedGraphIds.has(t.target_graph_id))?.target_name || '1 graph'
                  : `${selectedGraphIds.size} graphs selected`}
            </span>
            <span className="graph-selector-caret">{graphSelectorOpen ? '\u25B2' : '\u25BC'}</span>
          </button>
          {graphSelectorOpen && (
            <div className="graph-selector-dropdown">
              <label className="graph-selector-option graph-selector-auto">
                <input
                  type="checkbox"
                  checked={!selectedGraphIds || selectedGraphIds.size === 0}
                  onChange={() => {
                    onGraphSelectionChange(new Set());
                    setGraphSelectorOpen(false);
                  }}
                />
                <span>Auto Router</span>
                <span className="graph-selector-hint">Let the router decide</span>
              </label>
              <div className="graph-selector-divider" />
              {routerTargets.map((t) => {
                const id = t.target_graph_id;
                const checked = selectedGraphIds?.has(id) || false;
                return (
                  <label key={id} className="graph-selector-option">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        const next = new Set(selectedGraphIds || []);
                        if (checked) next.delete(id);
                        else next.add(id);
                        onGraphSelectionChange(next);
                      }}
                    />
                    <span>{t.target_name || id.substring(0, 8)}</span>
                    {t.target_type === 'process' && (
                      <span className="graph-selector-type-badge">Process</span>
                    )}
                  </label>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Input Area */}
      <div className="chat-input-area">
        <textarea
          ref={inputRef}
          className="chat-input"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type your question..."
          rows={2}
          disabled={isLoading}
        />
        <button
          className="chat-send-btn"
          onClick={handleSend}
          disabled={!inputValue.trim() || isLoading}
        >
          {isLoading ? (
            <span className="loading-spinner"></span>
          ) : (
            <span>Send</span>
          )}
        </button>
      </div>

      {/* Prompt History link + dropdown */}
      <div className="prompt-history-bar" ref={historyRef}>
        <button
          type="button"
          className="prompt-history-link"
          onClick={() => setHistoryOpen((v) => !v)}
          disabled={history.length === 0}
          title={
            history.length === 0
              ? 'No prompts yet -- send one to start your history'
              : 'Show recent prompts on this graph (last 50, this browser)'
          }
        >
          Prompt History ({history.length})
        </button>
        {historyOpen && history.length > 0 && (
          <ul className="prompt-history-dropdown">
            {history.map((p) => (
              <li key={p.ts} className="prompt-history-item">
                <button
                  type="button"
                  className="prompt-history-item-btn"
                  onClick={() => {
                    setInputValue(p.text);
                    setHistoryOpen(false);
                    inputRef.current?.focus();
                  }}
                  title={new Date(p.ts).toLocaleString()}
                >
                  <span className="prompt-history-item-text">
                    {p.text.length > 140 ? p.text.slice(0, 140) + '...' : p.text}
                  </span>
                  <span className="prompt-history-item-ts">
                    {new Date(p.ts).toLocaleString()}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

    </div>
  );
};

function getConfidenceColor(score) {
  if (score == null) return '#9ca3af';
  const pct = score * 100;
  if (pct >= 70) return '#16a34a';
  if (pct >= 40) return '#ca8a04';
  return '#dc2626';
}

// Individual chat message component
const ChatMessage = ({ message, onViewProcessSNNTrace, onViewInsight, onExplain, onSelectRouterGraph }) => {
  const [activeCite, setActiveCite] = useState(null);
  const [tooltipPos, setTooltipPos] = useState(null);

  const handleCiteClick = useCallback((e, cite) => {
    const rect = e.target.getBoundingClientRect();
    setActiveCite(cite);
    setTooltipPos({ top: rect.bottom + 4, left: rect.left });
  }, []);

  const closeCiteTooltip = useCallback(() => {
    setActiveCite(null);
    setTooltipPos(null);
  }, []);

  const needsSelection = message.metadata?.needs_user_selection
    && message.metadata?.routing?.close_candidates?.length > 0
    && !message.metadata?._selectionConfirmed;

  const renderMessageContent = () => {
    switch (message.type) {
      case 'user':
        return (
          <div className="message user-message">
            <div className="message-avatar">&#128100;</div>
            <div className="message-content">
              <div className="message-text">{message.content}</div>
              <div className="message-time">
                {new Date(message.timestamp).toLocaleTimeString()}
              </div>
            </div>
          </div>
        );
      
      case 'assistant': {
        const isConversational = message.metadata?.routing === 'CONVERSATIONAL';
        const score = message.metadata?.score;
        const trace = message.metadata?.execution_trace || {};
        const citationIndex = message.metadata?.citation_index || trace.citation_index || null;
        const sourceCount = (trace.context_assembly?.evidence_nodes || []).length
          + (trace.seed_nodes || []).length;
        const gates = trace.gates || [];
        const gatesSatisfied = gates.filter(g => g.fired).length;
        const hasExplainData = !isConversational && message.metadata &&
          (score != null || sourceCount > 0 || gates.length > 0);

        const multiSource = message.metadata?.multi_graph_source;
        const isMultiFirst = message.metadata?.multi_graph_index === 0;

        return (
          <div className="message assistant-message">
            <div className="message-avatar">{isConversational ? '\uD83D\uDCAC' : '\uD83E\uDDE0'}</div>
            <div className="message-content">
              {isMultiFirst && (
                <div style={{
                  fontSize: '12px', color: '#6b7280', fontWeight: 600,
                  marginBottom: '6px', paddingBottom: '4px', borderBottom: '1px solid #e5e7eb',
                }}>
                  Results from {message.metadata.multi_graph_total} graphs
                </div>
              )}
              {multiSource && (
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: '6px',
                  background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '6px',
                  padding: '4px 10px', marginBottom: '8px', fontSize: '12px', fontWeight: 600, color: '#1e40af',
                }}>
                  <span>{multiSource.graph_name || multiSource.graph_id?.substring(0, 8)}</span>
                  <span style={{ fontWeight: 400, color: '#3b82f6' }}>
                    ({(multiSource.routing_score * 100).toFixed(0)}% match)
                  </span>
                </div>
              )}
              <div className="message-text">
                <TextWithTables
                  text={message.content}
                  renderText={citationIndex?.length
                    ? (text, key) => renderTextWithCitations(text, citationIndex, handleCiteClick, key)
                    : undefined}
                />
              </div>
              {activeCite && (
                <CitationTooltip cite={activeCite} position={tooltipPos} onClose={closeCiteTooltip} />
              )}

              {/* Router graph/process selection card */}
              {needsSelection && (
                <div className="router-selection-card">
                  <div className="router-selection-options">
                    {message.metadata.routing.close_candidates.map((c) => (
                      <button
                        key={c.graph_id}
                        className="router-selection-option"
                        onClick={() => {
                          if (onSelectRouterGraph) {
                            onSelectRouterGraph(message.id, c.graph_id, c.graph_name || '', c.target_type || 'graph');
                          }
                        }}
                      >
                        <span className={`router-option-type-pill ${c.target_type === 'process' ? 'type-process' : 'type-graph'}`}>
                          {c.target_type === 'process' ? 'Process' : 'Graph'}
                        </span>
                        <span className="router-option-name">
                          {c.graph_name || c.graph_id.substring(0, 8)}
                        </span>
                        {c.description && (
                          <span className="router-option-tooltip">{c.description}</span>
                        )}
                        <span className="router-option-score">
                          {(c.score * 100).toFixed(0)}%
                        </span>
                        <span className="router-option-arrow">&#8250;</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Confirmed selection display */}
              {message.metadata?._selectionConfirmed && (
                <div className="router-selection-confirmed">
                  You selected: <strong>{message.metadata._selectedGraphName}</strong>
                </div>
              )}

              {/* Confidence bar (Tier 1) */}
              {!isConversational && !needsSelection && score != null && (
                <div className="ex-confidence-bar-wrap">
                  <div
                    className="ex-confidence-bar"
                    style={{
                      width: `${Math.round(score * 100)}%`,
                      background: getConfidenceColor(score),
                    }}
                    aria-label={`Confidence: ${(score * 100).toFixed(1)}%`}
                  />
                </div>
              )}

              {/* Inline badges row */}
              {message.metadata && !needsSelection && (
                <div className="message-metadata">
                  {isConversational ? (
                    <span className="metadata-item conversational-badge">
                      LLM Conversation
                    </span>
                  ) : (
                    <>
                      {score != null && (
                        <span className="metadata-item ex-score-badge" style={{ borderColor: getConfidenceColor(score) }}>
                          {(score * 100).toFixed(1)}%
                        </span>
                      )}
                      {sourceCount > 0 && (
                        <span className="metadata-item ex-source-pill">
                          {sourceCount} source{sourceCount !== 1 ? 's' : ''}
                        </span>
                      )}
                      {gates.length > 0 && (
                        <span className={`metadata-item ex-gate-badge ${gatesSatisfied === gates.length ? 'ex-gates-ok' : 'ex-gates-partial'}`}>
                          {gatesSatisfied === gates.length ? '\u2713' : '\u25CB'} {gatesSatisfied}/{gates.length} gates
                        </span>
                      )}
                      {message.metadata.intent && (
                        <span className="metadata-item ex-intent-badge">
                          {message.metadata.intent}
                        </span>
                      )}
                      {message.metadata.llm_usage && (() => {
                        const lu = message.metadata.llm_usage || {};
                        const ov = lu.overall || lu;
                        const model = ov.model || lu.model_name || 'LLM';
                        const provider = ov.provider_type || lu.provider_type;
                        const latency = ov.latency_ms != null ? ov.latency_ms : lu.response_time_ms;
                        const totalTokens = ov.total_tokens != null ? ov.total_tokens : lu.total_tokens;
                        const cost = ov.cost_usd != null ? ov.cost_usd : null;
                        const fmtCost = (c) => {
                          const v = Number(c || 0);
                          if (v <= 0) return null;
                          if (v < 0.0001) return '<$0.0001';
                          if (v < 0.01) return `$${v.toFixed(5)}`;
                          return `$${v.toFixed(4)}`;
                        };
                        const costLabel = fmtCost(cost);
                        return (
                          <span className="metadata-item" style={{
                            background: '#f0f9ff', color: '#0369a1', border: '1px solid #bae6fd',
                            padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 500,
                          }} title={`Estimated cost: ${costLabel || 'n/a'}`}>
                            {model}
                            {provider && provider !== 'openai' ? ` (${provider})` : ''}
                            {latency != null ? ` \u00B7 ${Math.round(latency)}ms` : ''}
                            {totalTokens != null ? ` \u00B7 ${Number(totalTokens).toLocaleString()} tok` : ''}
                            {costLabel ? ` \u00B7 ${costLabel}` : ''}
                          </span>
                        );
                      })()}
                      {message.metadata.rag_savings && message.metadata.rag_savings.headline && (() => {
                        const h = message.metadata.rag_savings.headline || {};
                        const pct = Number(h.savings_pct || 0);
                        if (pct <= 0) return null;
                        const onClick = message.metadata.insightId
                          // eslint-disable-next-line no-undef
                          ? () => window.open(`/user-home/graphs/build/${graphId}/build-tune/explainability?insight=${message.metadata.insightId}`, '_blank')
                          : undefined;
                        return (
                          <span className="metadata-item" style={{
                            background: '#ecfdf5', color: '#047857', border: '1px solid #6ee7b7',
                            padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 500,
                            cursor: onClick ? 'pointer' : 'default',
                          }}
                          onClick={onClick}
                          title={`Saved ~${pct.toFixed(1)}% vs Quality-Matched RAG (k=25, multi-pass). Click to see full breakdown.`}>
                            Saved {pct.toFixed(1)}% vs RAG
                          </span>
                        );
                      })()}
                      {message.metadata.routing && typeof message.metadata.routing === 'object' && message.metadata.routing.target_graph_id && !message.metadata.needs_user_selection && (
                        <span className="metadata-item" style={{
                          background: message.metadata.routing.target_type === 'process' ? '#fef3c7' : '#dbeafe',
                          color: message.metadata.routing.target_type === 'process' ? '#92400e' : '#1d4ed8',
                          border: `1px solid ${message.metadata.routing.target_type === 'process' ? '#fcd34d' : '#93c5fd'}`,
                          padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 500,
                        }}>
                          {message.metadata.routing.target_type === 'process' ? 'Routed to Process: ' : 'Routed to: '}
                          {message.metadata.routing.all_graph_scores?.[0]?.graph_name || message.metadata.routing.target_graph_id.substring(0, 8)}
                          {' '}({(message.metadata.routing.confidence * 100).toFixed(0)}%)
                          {message.metadata.routing.ambiguous && ' \u26A0 Ambiguous'}
                        </span>
                      )}
                      {message.metadata.execution_type === 'process' && (
                        <span className="metadata-item" style={{
                          background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d',
                          padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 500,
                        }}>
                          Process Execution
                        </span>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Action row: Explain + Full Trace */}
              {hasExplainData && (
                <div className="ex-actions-row">
                  {onExplain && (
                    <button
                      className="ex-explain-btn"
                      onClick={() => onExplain(message)}
                    >
                      <span className="ex-explain-icon">&#128161;</span>
                      Explain
                    </button>
                  )}
                  {message.metadata.insightId && onViewInsight && (
                    <button
                      className="execution-insight-link"
                      onClick={() => onViewInsight(message.metadata.insightId)}
                    >
                      <span className="insight-link-icon">&#128269;</span>
                      Full Trace
                    </button>
                  )}
                </div>
              )}

              {!isConversational && message.metadata?.insightId && (
                <FeedbackBar insightId={message.metadata.insightId} />
              )}

              {/* Save-failure badge: when /api/execution-insights returned an
                  error (typically 413 because the raw trace overflowed the
                  body limit), the chat used to silently lose the link. Show
                  a small inline note so the user knows why "Full Trace" /
                  Insights tab won't include this turn. */}
              {!isConversational && !message.metadata?.insightId
                && message.metadata?.insightSaveError && (
                <div
                  className="insight-save-error"
                  title={String(message.metadata.insightSaveError)}
                  style={{
                    marginTop: '6px',
                    fontSize: '11px',
                    color: '#92400e',
                    background: '#fef3c7',
                    border: '1px solid #fcd34d',
                    padding: '3px 8px',
                    borderRadius: '4px',
                    display: 'inline-block',
                  }}
                >
                  &#9888; Insight not saved: {String(message.metadata.insightSaveError)}
                </div>
              )}

              <div className="message-time">
                {new Date(message.timestamp).toLocaleTimeString()}
              </div>
            </div>
          </div>
        );
      }
      
      case 'error':
        return (
          <div className="message error-message">
            <div className="message-avatar">&#9888;&#65039;</div>
            <div className="message-content">
              <div className="message-text">{message.content}</div>
              <div className="message-time">
                {new Date(message.timestamp).toLocaleTimeString()}
              </div>
            </div>
          </div>
        );
      
      case 'loading':
        return (
          <div className="message loading-message">
            <div className="message-avatar">&#129504;</div>
            <div className="message-content">
              <div className="loading-dots">
                <span></span><span></span><span></span>
              </div>
            </div>
          </div>
        );
      
      case 'streaming':
        return (
          <div className="message streaming-message">
            <div className="message-avatar">&#129504;</div>
            <div className="message-content">
              {message.phase && (
                <div className="streaming-phase">{message.phase}</div>
              )}
              <div className="message-text">
                <TextWithTables text={message.content} />
                <span className="streaming-cursor"></span>
              </div>
            </div>
          </div>
        );
      
      case 'process_enrichment': {
        const enrichData = message.enrichmentData || {};
        const processIds = Object.keys(enrichData);

        const HIDDEN_KEYS = new Set([
          'enrichment_context', 'enrichment_summary', 'medical_policy_block',
          'code_lookup_trace', 'medical_policy_lookup_trace', 'service_specific_lookup_trace',
          'case_text', 'normalized_case',
        ]);

        const fmtCurrency = (v) => v != null && v !== 0 ? `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2 })}` : null;
        const fmtDate = (d) => {
          if (!d) return null;
          try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); } catch { return d; }
        };
        const archetypeLabel = (a) => (a || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

        const allOutputs = {};
        processIds.forEach(pid => {
          const fo = (enrichData[pid] || {}).final_outputs || {};
          Object.entries(fo).forEach(([k, v]) => { if (!HIDDEN_KEYS.has(k) && !allOutputs[k]) allOutputs[k] = v; });
        });

        const cef = allOutputs.case_evidence_frame || {};
        const codeCtx = allOutputs.code_context || {};
        const medPolicy = allOutputs.medical_policy_lookup_results || {};
        const svcLookups = allOutputs.service_specific_lookup_results || {};

        const hasCef = !!(cef.claim_lines || cef.denials || cef.coverage);
        const hasStructured = hasCef || Object.keys(codeCtx).length > 0 || Object.keys(medPolicy).length > 0;

        const coverage = cef.coverage || {};
        const payerProfile = codeCtx.payer_profile || {};
        const isMedicare = payerProfile.is_medicare || false;
        const claimLines = cef.claim_lines || [];
        const denials = cef.denials || [];
        const appealDeadline = codeCtx.appeal_deadline || medPolicy.appeal_deadline || {};

        const rawJson = {};
        processIds.forEach(pid => { rawJson[pid] = enrichData[pid]; });

        if (!hasStructured) {
          return (
            <div className="message assistant-message">
              <div className="message-avatar" style={{ fontSize: '16px' }}>&#128269;</div>
              <div className="message-content">
                <div className="enrichment-results-container">
                  <div className="enrichment-results-header">Pre-Enrichment Lookup Results</div>
                  {processIds.map(pid => {
                    const pr = enrichData[pid];
                    const outputs = pr.final_outputs || {};
                    const outputKeys = Object.keys(outputs).filter(k => !HIDDEN_KEYS.has(k));
                    return (
                      <details key={pid} className="enrichment-card" open>
                        <summary className="enrichment-card-header">Process {pid.slice(0, 8)} &mdash; {pr.status || 'unknown'}</summary>
                        <div className="enrichment-card-body">
                          {outputKeys.length === 0 && <p style={{ color: '#9ca3af', fontStyle: 'italic' }}>No outputs produced</p>}
                          {outputKeys.map(ok => (
                            <details key={ok} className="enrichment-output-section">
                              <summary className="enrichment-output-key">{ok}</summary>
                              <pre className="enrichment-output-value">
                                {typeof outputs[ok] === 'object' ? JSON.stringify(outputs[ok], null, 2) : String(outputs[ok])}
                              </pre>
                            </details>
                          ))}
                        </div>
                      </details>
                    );
                  })}
                </div>
                <div className="message-time">{new Date(message.timestamp).toLocaleTimeString()}</div>
              </div>
            </div>
          );
        }

        return (
          <div className="message assistant-message">
            <div className="message-avatar" style={{ fontSize: '16px' }}>&#128269;</div>
            <div className="message-content">
              <div className="enrichment-results-container">
                <div className="enrichment-results-header">Pre-Enrichment Lookup Results</div>

                {/* ---- Case Summary ---- */}
                <div className="enrich-section">
                  <div className="enrich-section-title">Case Summary</div>
                  <div className="enrich-kv-grid">
                    {cef.archetype && (
                      <><span className="enrich-label">Category</span><span className="enrich-value"><span className="enrich-badge">{archetypeLabel(cef.archetype)}</span></span></>
                    )}
                    {cef.complexity_level && (
                      <><span className="enrich-label">Complexity</span><span className="enrich-value">{cef.complexity_level}</span></>
                    )}
                    {(coverage.payer_name || payerProfile.payer_name) && (
                      <><span className="enrich-label">Payer</span>
                        <span className="enrich-value">
                          {coverage.payer_name || payerProfile.payer_name}
                          {isMedicare && <span className="enrich-badge enrich-badge-blue" style={{ marginLeft: 6 }}>Medicare</span>}
                        </span>
                      </>
                    )}
                    {coverage.plan_type && (
                      <><span className="enrich-label">Plan</span><span className="enrich-value">{coverage.plan_type}</span></>
                    )}
                    {cef.patient?.patient_id && cef.patient.patient_id !== 'unknown' && (
                      <><span className="enrich-label">Patient ID</span><span className="enrich-value">{cef.patient.patient_id}</span></>
                    )}
                    {appealDeadline.days && (
                      <><span className="enrich-label">Appeal Deadline</span><span className="enrich-value">{appealDeadline.days} days ({appealDeadline.basis || ''})</span></>
                    )}
                  </div>
                  {cef.summary && <div className="enrich-summary-text">{cef.summary}</div>}
                </div>

                {/* ---- Claim Lines ---- */}
                {claimLines.length > 0 && (
                  <div className="enrich-section">
                    <div className="enrich-section-title">Claim Lines</div>
                    <table className="enrich-table">
                      <thead>
                        <tr>
                          <th>#</th><th>CPT/HCPCS</th><th>Diagnosis (ICD-10)</th><th>DOS</th><th>POS</th><th>Billed</th><th>Allowed</th><th>Denied</th>
                        </tr>
                      </thead>
                      <tbody>
                        {claimLines.map((cl, i) => (
                          <tr key={i}>
                            <td>{cl.line_no || i + 1}</td>
                            <td className="enrich-code">{cl.cpt_or_hcpcs || '—'}</td>
                            <td>{(cl.icd10_pointers || []).join(', ') || '—'}</td>
                            <td>{fmtDate(cl.service_date) || '—'}</td>
                            <td>{cl.place_of_service || '—'}</td>
                            <td>{fmtCurrency(cl.billed_amount) || '—'}</td>
                            <td>{fmtCurrency(cl.allowed_amount) || '—'}</td>
                            <td>{fmtCurrency(cl.denied_amount) || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* ---- Denial Codes ---- */}
                {denials.length > 0 && (
                  <div className="enrich-section">
                    <div className="enrich-section-title">Denial Reason Codes</div>
                    {denials.map((d, i) => (
                      <div key={i} className="enrich-denial-row">
                        <span className="enrich-code-pill">CARC {d.carc || '?'}</span>
                        {d.rarc && <span className="enrich-code-pill enrich-code-pill-secondary">RARC {d.rarc}</span>}
                        {d.payer_note && <span className="enrich-denial-note">{d.payer_note}</span>}
                      </div>
                    ))}
                  </div>
                )}

                {/* ---- Lookup Results ---- */}
                {(Object.keys(codeCtx).length > 0 || Object.keys(medPolicy).length > 0 || Object.keys(svcLookups).length > 0) && (
                  <div className="enrich-section">
                    <div className="enrich-section-title">Lookup Results</div>

                    {/* Code Enrichment */}
                    {codeCtx.cpt_hcpcs && codeCtx.cpt_hcpcs.length > 0 && (
                      <div className="enrich-subsection">
                        <div className="enrich-subsection-title">Code Enrichment</div>
                        {codeCtx.cpt_hcpcs.map((item, i) => (
                          <div key={i} className="enrich-kv-row">
                            <span className="enrich-code">{item.code}</span>
                            {item.modifiers?.length > 0 && <span className="enrich-tag">Mods: {item.modifiers.join(', ')}</span>}
                            {item.pos && <span className="enrich-tag">POS {item.pos}</span>}
                          </div>
                        ))}
                        {payerProfile.payer_name && (
                          <div className="enrich-kv-row">
                            <span className="enrich-label">Payer Profile:</span>
                            <span>{payerProfile.payer_name}</span>
                            {isMedicare && <span className="enrich-badge enrich-badge-blue" style={{ marginLeft: 4 }}>Medicare</span>}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Medical Policy */}
                    {Object.keys(medPolicy).length > 0 && (
                      <div className="enrich-subsection">
                        <div className="enrich-subsection-title">Medical Policy</div>
                        {medPolicy.lcd_matches?.length > 0 && (
                          <div className="enrich-kv-row"><span className="enrich-label">LCD Matches:</span><span>{JSON.stringify(medPolicy.lcd_matches)}</span></div>
                        )}
                        {medPolicy.ncd_matches?.length > 0 && (
                          <div className="enrich-kv-row"><span className="enrich-label">NCD Matches:</span><span>{JSON.stringify(medPolicy.ncd_matches)}</span></div>
                        )}
                        {medPolicy.article_matches?.length > 0 && (
                          <div className="enrich-kv-row"><span className="enrich-label">Article Matches:</span><span>{JSON.stringify(medPolicy.article_matches)}</span></div>
                        )}
                        {medPolicy.required_documentation?.length > 0 && (
                          <div className="enrich-kv-row"><span className="enrich-label">Required Docs:</span><span>{JSON.stringify(medPolicy.required_documentation)}</span></div>
                        )}
                        {medPolicy.clinical_evidence_requirements?.length > 0 && (
                          <div className="enrich-kv-row"><span className="enrich-label">Clinical Evidence:</span><span>{JSON.stringify(medPolicy.clinical_evidence_requirements)}</span></div>
                        )}
                        {medPolicy.policy_source_missing && (
                          <div className="enrich-warning">No structured medical policy source found offline. Graph query will attempt to retrieve policy data.</div>
                        )}
                      </div>
                    )}

                    {/* Service-Specific */}
                    {Object.keys(svcLookups).length > 0 && (() => {
                      const entries = Object.entries(svcLookups).filter(([, v]) => v && Object.keys(v).length > 0);
                      if (entries.length === 0) return null;
                      return (
                        <div className="enrich-subsection">
                          <div className="enrich-subsection-title">Service-Specific</div>
                          {entries.map(([k, v]) => (
                            <div key={k} className="enrich-kv-row">
                              <span className="enrich-label">{k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}:</span>
                              <span>{typeof v === 'object' ? Object.entries(v).map(([vk, vv]) => `${vk}: ${vv}`).join(', ') : String(v)}</span>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* ---- Raw Details (collapsed) ---- */}
                <details className="enrich-raw-details">
                  <summary className="enrich-raw-summary">View Raw Data</summary>
                  <pre className="enrich-raw-json">{JSON.stringify(rawJson, null, 2)}</pre>
                </details>
              </div>
              <div className="message-time">{new Date(message.timestamp).toLocaleTimeString()}</div>
            </div>
          </div>
        );
      }

      case 'process_polling': {
        const pd = message.processPollingData || {};
        const progressPct = pd.totalNodes > 0 ? Math.round((pd.completedNodes / pd.totalNodes) * 100) : 0;
        return (
          <div className="message assistant-message">
            <div className="message-avatar">&#9881;&#65039;</div>
            <div className="message-content">
              <div className="process-polling-status">
                <div className="process-polling-header">
                  <span className="process-polling-label">Executing: {pd.processName || 'Process'}</span>
                  <span className="process-polling-indicator">Processing...</span>
                </div>
                {pd.totalNodes > 0 && (
                  <>
                    <div className="process-polling-bar-wrap">
                      <div className="process-polling-bar" style={{ width: `${progressPct}%` }} />
                    </div>
                    <div className="process-polling-counts">
                      {pd.completedNodes}/{pd.totalNodes} nodes
                      {pd.activeNodes > 0 && ` (${pd.activeNodes} active)`}
                    </div>
                  </>
                )}
              </div>
              <div className="message-time">
                {new Date(message.timestamp).toLocaleTimeString()}
              </div>
            </div>
          </div>
        );
      }

      case 'process_assistant':
        return (
          <div className={`message process-message ${message.processData?.status || ''}`}>
            <div className="message-avatar">&#9881;&#65039;</div>
            <div className="message-content">
              <ProcessMessageContent 
                processData={message.processData}
                onViewSNNTrace={onViewProcessSNNTrace}
              />
              <div className="message-time">
                {new Date(message.timestamp).toLocaleTimeString()}
              </div>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return renderMessageContent();
};


// ---------------------------------------------------------------------------
// FeedbackBar -- thumbs up/down + always-visible free-text comment.
// ---------------------------------------------------------------------------
const FeedbackBar = ({ insightId }) => {
  const [feedbackId, setFeedbackId] = useState(null);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [savedComment, setSavedComment] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState(null);
  const [hydrated, setHydrated] = useState(false);

  // Hydrate any existing feedback on mount so the user can edit/clear.
  useEffect(() => {
    let cancelled = false;
    if (!insightId) {
      setHydrated(true);
      return undefined;
    }
    (async () => {
      try {
        const res = await apiService.listInsightFeedback(insightId);
        if (cancelled) return;
        const rows = res?.feedback || [];
        const existing = rows[0];
        if (existing) {
          setFeedbackId(existing.feedback_id);
          setRating(existing.rating || 0);
          setComment(existing.comment || '');
          setSavedComment(existing.comment || '');
          setSavedAt(existing.updated_at || existing.created_at || null);
        }
      } catch (e) {
        // Non-fatal -- the bar still works for new feedback.
      } finally {
        if (!cancelled) setHydrated(true);
      }
    })();
    return () => { cancelled = true; };
  }, [insightId]);

  const persist = useCallback(async ({ nextRating, nextComment, fromThumb = false }) => {
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      if (feedbackId) {
        const payload = {};
        if (nextRating !== undefined) payload.rating = nextRating;
        if (nextComment !== undefined) payload.comment = nextComment ?? '';
        const res = await apiService.updateInsightFeedback(feedbackId, payload);
        if (res?.success) {
          if (nextRating !== undefined) setRating(nextRating);
          if (nextComment !== undefined) setSavedComment(nextComment ?? '');
          setSavedAt(res.feedback?.updated_at || new Date().toISOString());
        }
      } else {
        const payload = {
          rating: nextRating !== undefined ? nextRating : rating,
          comment: nextComment !== undefined ? nextComment : (comment || null),
        };
        const res = await apiService.saveInsightFeedback(insightId, payload);
        if (res?.success && res.feedback?.feedback_id) {
          setFeedbackId(res.feedback.feedback_id);
          setRating(payload.rating || 0);
          setSavedComment(payload.comment || '');
          setSavedAt(res.feedback.created_at || new Date().toISOString());
        }
      }
    } catch (e) {
      console.error('Feedback save failed:', e);
      setError(e?.response?.data?.error || 'Could not save feedback');
    } finally {
      setSaving(false);
    }
  }, [comment, feedbackId, insightId, rating, saving]);

  const handleThumb = (newRating) => {
    // Toggle off when clicking the active thumb again.
    const target = rating === newRating ? 0 : newRating;
    persist({ nextRating: target, fromThumb: true });
  };

  const handleSubmitComment = () => {
    const trimmed = comment.trim();
    persist({ nextComment: trimmed.length ? trimmed : null });
  };

  const commentDirty = (comment || '') !== (savedComment || '');
  const hasContent = rating !== 0 || (savedComment || '').length > 0;

  return (
    <div className="message-feedback">
      <div className="feedback-row">
        <button
          type="button"
          className={`feedback-thumb ${rating === 1 ? 'is-active is-positive' : ''}`}
          onClick={() => handleThumb(1)}
          disabled={saving || !hydrated}
          title="This answer was helpful"
          aria-label="Thumbs up"
          aria-pressed={rating === 1}
        >
          &#128077;
        </button>
        <button
          type="button"
          className={`feedback-thumb ${rating === -1 ? 'is-active is-negative' : ''}`}
          onClick={() => handleThumb(-1)}
          disabled={saving || !hydrated}
          title="This answer needs work"
          aria-label="Thumbs down"
          aria-pressed={rating === -1}
        >
          &#128078;
        </button>
        <span className="feedback-row-label">Leave feedback</span>
        {savedAt && hasContent && !commentDirty && (
          <span className="feedback-status">Saved &middot; {new Date(savedAt).toLocaleTimeString()}</span>
        )}
      </div>

      <div className="feedback-comment-row">
        <textarea
          className="feedback-textarea"
          placeholder="Tell us what worked, what didn't, or what the answer should have been"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          rows={2}
          disabled={saving || !hydrated}
        />
        <div className="feedback-comment-actions">
          <button
            type="button"
            className="feedback-submit"
            onClick={handleSubmitComment}
            disabled={saving || !hydrated || !commentDirty}
          >
            {feedbackId && savedComment ? 'Update comment' : 'Submit comment'}
          </button>
          {feedbackId && savedComment && (
            <button
              type="button"
              className="feedback-cancel"
              onClick={() => {
                setComment('');
                persist({ nextComment: null });
              }}
              disabled={saving}
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {error && <span className="feedback-error">{error}</span>}
    </div>
  );
};


export default TestGraphChat;
